import { $FixMe } from '@eventbrite/legacy-infra.ts-utils';
import i18next, { i18n as I18nInstance } from 'i18next';
import { GenericLazyString } from './genericLazyString';
import { ParentLanguageCode } from './constants';

export const i18n: I18nInstance = i18next.createInstance();
const I18N_NEXT_INITIALIZED = { current: false };

/**
 * I18n initialization
 *  - Uses all available translations downloaded from Transifex at build time.
 *  - If a locale is provided like 'es-ES' is not found, then an 'es' translation will be looked up.
 *  - If no 'es' translation is found, then an 'en' translation will be looked up
 *  - If no 'en' translation is found, then the provided key will be returned as is.
 */
const initializeI18n = () => {
    if (!I18N_NEXT_INITIALIZED.current) {
        i18n.init({
            lng: 'en',
            fallbackLng: 'en',
            returnEmptyString: false,
            keySeparator: false,
            nsSeparator: false,
            pluralSeparator: void 0,
            contextSeparator: void 0,
            initImmediate: false, // This one prevents a setTimeout(fn, 0)
            interpolation: {
                escapeValue: false,
                prefix: '%(',
                suffix: ')s',
            },
        });
        I18N_NEXT_INITIALIZED.current = true;
    }
};

/**
 * Locale files generated by transifex tooling always use '_' as the region separator,
 * but i18next expects them to be to be separated by '-'.
 * @param {string} locale the locale string to normalize
 */
const normalizeLocale = (locale: string) => locale.replace('_', '-');

/**
 * gettext is the main translation function.
 *
 * @param key the key, string or template string of the message to translate
 * @param interpolation an object used for interpolation in the template string.
 * @returns a GenericLazyString instance that will resolve to the active language when used.
 */
export const gettext = (key: string, interpolation?: Record<string, $FixMe>) => {
    initializeI18n();

    return new GenericLazyString((): string => {
        /*
         * When no interpolation object is provided, we need to preserve the placeholders
         * This is for backward compatibility with existing code relying on this behavior.
         */
        let interp: any = {
            interpolation: { suffix: '####', prefix: '####' },
        };

        if (interpolation) {
            interp = Object.fromEntries(
                Object.entries(interpolation).map(([key, value]) =>
                    value instanceof GenericLazyString ? [key, value.toString()] : [key, value],
                ),
            );
        }
        const text = i18n.t(key, interp) as string;

        return text;
    });
};

/**
 * Changes the active language for all subsequent calls to gettext()
 *
 * @param {string} language the new desired language for translations
 */
export const setLanguage = (language: string) => {
    initializeI18n();
    i18n.changeLanguage(normalizeLocale(language));
};

/**
 * Setup translations synchronously. Use this when you already have the translations loaded.
 * Perfect for Next.js getServerSideProps where you fetch translations server-side.
 *
 * @param options.parentLocale The parent locale string, e.g. 'es_ES' or 'en_US'
 * @param options.translations The already-loaded translations object
 * @example
 * // In getServerSideProps:
 * const translations = await fetch(...).then(r => r.json());
 *
 * // In render/component:
 * setup({
 *   parentLocale: 'es_ES',
 *   translations: translations
 * });
 */
export const setup = ({
    parentLocale,
    translations,
}: {
    parentLocale: ParentLanguageCode;
    translations: Record<string, string>;
}) => {
    initializeI18n();
    setLanguage(parentLocale);
    // Add resource bundle with overwrite to ensure translations are updated
    i18n.addResourceBundle(normalizeLocale(parentLocale), 'translation', translations, true, true);
};
